import React, { useCallback, useEffect, useRef } from 'react'
import { useNavigate, useParams } from 'react-router'
import { BusinessInfoInput, CompletionStatus, CreditApplicationBusinessInfo, SectionName } from '_gql'
import { yupResolver } from '@hookform/resolvers/yup'
import states from 'states-us'
import { useEventListener } from 'usehooks-ts'

import useNameFormatter from '@divvy-web/hooks.usenameformatter'
import { FormattedMessage } from '@divvy-web/i18n'
import { AddressForm, FormCheckboxInput } from '@divvy-web/skylab.form'
import { DeepPartial, FormProvider, useController, useForm, useFormState } from '@divvy-web/skylab.react-hook-form'
import { TOAST_TYPE_DANGER, useToast } from '@divvy-web/skylab.toast'

import { useApolloQueryErrorHandling } from '@sputnik/apollo/useApolloQueryErrorHandling'
import useGetCreditAppTypes from '@sputnik/hooks/useGetCreditAppTypes'
import CreditApplicationPageLayout from '@sputnik/pages/CreditApplicationPages/CreditApplicationPageLayout'
import FormAutoSubmitProvider from '@sputnik/pages/CreditApplicationPages/FormAutoSubmit'
import { useApplicationProgress } from '@sputnik/pages/CreditApplicationPages/Navigation/useApplicationProgress'
import { hasMissingRequiredField } from '@sputnik/pages/CreditApplicationPages/ReviewForm/utils'
import { PATHNAME_CONSTS } from '@sputnik/resources/constants'
import { useWebTracking } from '@sputnik/tracking/WebTrackingContext'

import BusinessAddressFormMarketingContent from './BusinessAddressFormMarketingContent'
import {
  type BusinessAddressFormValidationSchema,
  businessAddressFormValidationSchema,
} from './businessAddressFormValidationSchema'
import { useGetBusinessAddressLazyQuery } from './GetBusinessAddress.gql'
import styles from './styles.module.scss'
import { useUpdateBusinessAddress } from './UpdateBusinessAddress.gql'

const getFormValues = ({
  physicalAddress,
  mailingAddress,
  mailingPhysicalAddressesEqual,
}: Pick<CreditApplicationBusinessInfo, 'physicalAddress' | 'mailingAddress' | 'mailingPhysicalAddressesEqual'>) => {
  return {
    mailingPhysicalAddressesEqual: mailingPhysicalAddressesEqual ?? true,
    physicalAddress: physicalAddress
      ? {
          street: physicalAddress.street ?? '',
          streetAdditional: physicalAddress.streetAdditional ?? '',
          city: physicalAddress.city ?? '',
          state: physicalAddress.state ?? '',
          postalCode: physicalAddress.postalCode ?? '',
          countryCode: physicalAddress.countryCode,
        }
      : null,
    mailingAddress: mailingAddress
      ? {
          street: mailingAddress.street ?? '',
          streetAdditional: mailingAddress.streetAdditional ?? '',
          city: mailingAddress.city ?? '',
          state: mailingAddress.state ?? '',
          postalCode: mailingAddress.postalCode ?? '',
          countryCode: mailingAddress.countryCode,
        }
      : null,
  }
}

const BusinessAddressForm = () => {
  const { appId } = useParams()
  const navigate = useNavigate()
  const [getClassName] = useNameFormatter('BusinessAddressForm')
  const showToast = useToast()
  const { isMigration, isMajorAccountChange } = useGetCreditAppTypes({
    salesforceCreditId: appId ?? '',
  })

  const physicalAddressStateDropdownRef = useRef<HTMLInputElement | null>(null)
  const mailingAddressStateDropdownRef = useRef<HTMLInputElement | null>(null)

  /* tracking start */

  const { track } = useWebTracking()

  useEffect(() => {
    track('se_signup__application_business_info_address__presented')
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  /* tracking end */

  const completionStatus = useApplicationProgress({ sectionName: SectionName.BUSINESS_INFO })

  const [getBusinessAddress, { error }] = useGetBusinessAddressLazyQuery()

  useApolloQueryErrorHandling({
    error,
    toast: {
      message: (
        <FormattedMessage
          defaultMessage='Failed to load business address data'
          id='sputnik.BusinessAddressForm__ArGQET'
        />
      ),
    },
  })

  // mutation to update the form field sections here.
  const [updateBusinessAddress] = useUpdateBusinessAddress({
    onError: () => {
      showToast(
        TOAST_TYPE_DANGER,
        <FormattedMessage
          defaultMessage='Failed to update business address data'
          id='sputnik.BusinessAddressForm__6tsvoT'
        />,
        { className: 'fs-unmask' },
      )
    },
  })

  const methods = useForm<BusinessAddressFormValidationSchema>({
    mode: 'all',
    defaultValues: async () => {
      const { data } = await getBusinessAddress({
        variables: { creditApplicationId: appId ?? '' },
      })

      const { physicalAddress, mailingAddress, mailingPhysicalAddressesEqual } =
        data?.creditApplication?.businessInfo ?? {}

      const emptyAddressObject = {
        __typename: 'CreditApplicationAddress' as const,
        street: '',
        streetAdditional: '',
        city: '',
        state: '',
        postalCode: '',
        countryCode: 'US',
      }

      if (!mailingAddress && !mailingPhysicalAddressesEqual) {
        return getFormValues({
          physicalAddress: physicalAddress ?? emptyAddressObject,

          mailingAddress: emptyAddressObject,
          mailingPhysicalAddressesEqual: true,
        })
      } else {
        return getFormValues(
          data?.creditApplication?.businessInfo ?? {
            mailingPhysicalAddressesEqual: true,
            physicalAddress: emptyAddressObject,
            mailingAddress: emptyAddressObject,
          },
        )
      }
    },
    resolver: yupResolver(businessAddressFormValidationSchema),
  })
  const { control, reset, getValues } = methods
  const { isLoading } = useFormState({ control })

  const {
    field: { onChange: onPhysicalAddressStateChange },
  } = useController({
    control,
    name: 'physicalAddress.state',
  })
  const {
    field: { onChange: onMailingAddressStateChange },
  } = useController({
    control,
    name: 'mailingAddress.state',
  })

  const updateState = useCallback(
    (onChange: (value: string) => void) => (event: Event) => {
      const value = (event.target as HTMLInputElement)?.value

      if (!value) {
        return
      }

      const stateSelected = states.find(({ abbreviation, name }) => value === abbreviation || value === name)

      if (stateSelected) {
        onChange(stateSelected.abbreviation)
      }
    },
    [],
  )

  //@ts-expect-error - library has not been updated for React 19, might want to migrate to something else because it appears unmaintained now
  useEventListener('change', updateState(onPhysicalAddressStateChange), physicalAddressStateDropdownRef)

  //@ts-expect-error - library has not been updated for React 19, might want to migrate to something else because it appears unmaintained now
  useEventListener('change', updateState(onMailingAddressStateChange), mailingAddressStateDropdownRef)

  const saveBusinessAddressData = useCallback(
    async (data: DeepPartial<BusinessAddressFormValidationSchema>) => {
      const [arePhysicalAndMailingAddressesEqual, existingPhyiscalAddress] = getValues([
        'mailingPhysicalAddressesEqual',
        'physicalAddress',
      ])
      const { physicalAddress: updatedPhysicalAddress, mailingAddress } = data
      const input: BusinessInfoInput = {
        physicalAddress: updatedPhysicalAddress,
        mailingAddress: arePhysicalAndMailingAddressesEqual === true ? existingPhyiscalAddress : mailingAddress,
      }

      await updateBusinessAddress({
        variables: {
          creditApplicationId: appId ?? '',
          businessInfo: input,
        },
        onCompleted: (updatedData) => {
          // tracking for business address section and business info section as a whole
          const businessInfoSectionComplete = updatedData.updateCreditApplication?.savedSectionsInfo?.find(
            (section) =>
              section?.sectionName === SectionName.BUSINESS_INFO &&
              section?.completionStatus === CompletionStatus.COMPLETED,
          )

          if (businessInfoSectionComplete && completionStatus !== CompletionStatus.COMPLETED) {
            track('se_signup__application_business_info_section_progress__completed', {
              is_complete: true,
            })
          }

          const hasMissingInfo = hasMissingRequiredField([
            updatedData?.updateCreditApplication?.businessInfo?.physicalAddress?.street,
            updatedData?.updateCreditApplication?.businessInfo?.physicalAddress?.city,
            updatedData?.updateCreditApplication?.businessInfo?.physicalAddress?.state,
            updatedData?.updateCreditApplication?.businessInfo?.physicalAddress?.postalCode,
          ])

          if (!hasMissingInfo) {
            track('se_signup__application_business_info_address_section_progress__completed', {
              is_complete: true,
            })
          }

          // logic for sending individual field tracking
          if (arePhysicalAndMailingAddressesEqual) {
            track('se_signup__application_business_info_address_mail_address_same_as_biz_address__submitted', {
              value_present: true,
            })
          }

          if (data.physicalAddress?.street) {
            track('se_signup__application_business_info_address_physical_address_street__submitted', {
              value_present: true,
            })
          }

          if (data.physicalAddress?.streetAdditional) {
            track('se_signup__application_business_info_address_physical_address_street_additional__submitted', {
              value_present: true,
            })
          }

          if (data.physicalAddress?.city) {
            track('se_signup__application_business_info_physical_address_city__submitted', {
              value_present: true,
            })
          }

          if (data.physicalAddress?.state) {
            track('se_signup__application_business_info_address_physical_state__submitted', {
              value_present: true,
            })
          }

          if (data.physicalAddress?.postalCode) {
            track('se_signup__application_business_info_address_physical_zip__submitted', {
              value_present: true,
            })
          }

          if (data.mailingAddress?.street) {
            track('se_signup__application_business_info_address_mailing_address_street__submitted', {
              value_present: true,
            })
          }

          if (data.mailingAddress?.streetAdditional) {
            track('se_signup__application_business_info_address_mailing_address_street_additional__submitted', {
              value_present: true,
            })
          }

          if (data.mailingAddress?.city) {
            track('se_signup__application_business_info_mailing_address_city__submitted', {
              value_present: true,
            })
          }

          if (data.mailingAddress?.state) {
            track('se_signup__application_business_info_address_mailing_state__submitted', {
              value_present: true,
            })
          }

          if (data.mailingAddress?.postalCode) {
            track('se_signup__application_business_info_address_mailing_zip__submitted', {
              value_present: true,
            })
          }

          reset(data)
        },
      })
    },
    [appId, completionStatus, getValues, reset, track, updateBusinessAddress],
  )

  const {
    field: { value: areMailingAndPhysicalAddressesEqual, onChange: onMailingPhysicalAddressesEqualChange },
  } = useController({
    control,
    name: 'mailingPhysicalAddressesEqual',
  })

  const {
    field: { onChange: onMailingAddressChange },
  } = useController({
    control,
    name: 'mailingAddress',
  })

  const onNextClick = () => {
    track('se_signup__application_business_info_address_next__fired')
    if (isMigration || isMajorAccountChange) {
      navigate(`../${PATHNAME_CONSTS.PEOPLE_INFO_PATH}`)
      return
    }

    navigate(`../${PATHNAME_CONSTS.FINANCIAL_DETAILS_PATH}`)
  }

  return (
    <div className={`${styles[getClassName('')]} fs-unmask`}>
      <FormProvider {...methods}>
        <div className={styles[getClassName('form')]}>
          <FormAutoSubmitProvider updateHandler={saveBusinessAddressData} />
          <CreditApplicationPageLayout
            marketing={<BusinessAddressFormMarketingContent />}
            pageTitle={
              <FormattedMessage
                defaultMessage='List your business address'
                id='sputnik.BusinessAddressForm__IgpuMm'
              />
            }
            onBackClick={() => {
              track('se_signup__application_business_info_address_back__fired')
              navigate(`../${PATHNAME_CONSTS.NAME_AND_OWNERSHIP_PATH}`)
            }}
            onNextClick={onNextClick}
          >
            <AddressForm
              className='fs-mask'
              control={control}
              fields={{
                city: {
                  autoComplete: 'billing address-level2',
                  disabled: isLoading,
                  label: (
                    <span className='fs-unmask'>
                      <FormattedMessage
                        defaultMessage='City'
                        id='sputnik.BusinessAddressForm__TE4fIS'
                      />
                    </span>
                  ),
                  name: 'physicalAddress.city',
                },
                state: {
                  disabled: isLoading,
                  label: (
                    <span className='fs-unmask'>
                      <FormattedMessage
                        defaultMessage='State'
                        id='sputnik.BusinessAddressForm__ku+mDU'
                      />
                    </span>
                  ),
                  name: 'physicalAddress.state',
                  placeholder: 'Select',
                  autoSelectOnBlur: false,
                  ref: physicalAddressStateDropdownRef,
                  inputProps: {
                    autoComplete: 'billing address-level1',
                    name: 'physicalAddress.state',
                    id: 'physicalAddress.state',
                    'aria-labelledby': 'physicalAddress.state',
                  },
                },
                street1: {
                  autoComplete: 'billing address-line1',
                  disabled: isLoading,
                  label: (
                    <span className='fs-unmask'>
                      <FormattedMessage
                        defaultMessage='Physical business address'
                        id='sputnik.BusinessAddressForm__9WMdEt'
                      />
                    </span>
                  ),
                  name: 'physicalAddress.street',
                },
                street2: {
                  autoComplete: 'billing address-line2',
                  disabled: isLoading,
                  label: (
                    <span className='fs-unmask'>
                      <FormattedMessage
                        defaultMessage='Physical business address line 2'
                        id='sputnik.BusinessAddressForm__m2Jm0n'
                      />
                    </span>
                  ),
                  name: 'physicalAddress.streetAdditional',
                },
                zipCode: {
                  autoComplete: 'billing postal-code',
                  disabled: isLoading,
                  label: (
                    <span className='fs-unmask'>
                      <FormattedMessage
                        defaultMessage='ZIP code'
                        id='sputnik.BusinessAddressForm__iZL8VQ'
                      />
                    </span>
                  ),
                  name: 'physicalAddress.postalCode',
                },
              }}
              googleMapsApiKey={window.__DivvyEnvironment.GOOGLE_PLACES_API_KEY}
            />
            <FormCheckboxInput
              checkboxLabel={
                <FormattedMessage
                  defaultMessage='Mailing address is the same as physical address'
                  id='sputnik.BusinessAddressForm__hhODeG'
                />
              }
              className='fs-unmask'
              control={control}
              disabled={isLoading}
              name='mailingPhysicalAddressesEqual'
              onChange={(e) => {
                onMailingPhysicalAddressesEqualChange(e)
                onMailingAddressChange({
                  street: '',
                  streetAdditional: '',
                  city: '',
                  state: '',
                  postalCode: '',
                  countryCode: 'US',
                })
              }}
            />
            {!isLoading && !areMailingAndPhysicalAddressesEqual && (
              <AddressForm
                className='fs-mask'
                control={control}
                fields={{
                  city: {
                    autoComplete: 'shipping address-level2',
                    disabled: isLoading,
                    label: (
                      <span className='fs-unmask'>
                        <FormattedMessage
                          defaultMessage='City'
                          id='sputnik.BusinessAddressForm__TE4fIS'
                        />
                      </span>
                    ),
                    name: 'mailingAddress.city',
                  },
                  state: {
                    disabled: isLoading,
                    label: (
                      <span className='fs-unmask'>
                        <FormattedMessage
                          defaultMessage='State'
                          id='sputnik.BusinessAddressForm__ku+mDU'
                        />
                      </span>
                    ),
                    name: 'mailingAddress.state',
                    autoSelectOnBlur: false,
                    placeholder: 'Select',
                    ref: mailingAddressStateDropdownRef,
                    inputProps: {
                      autoComplete: 'shipping address-level1',
                      name: 'mailingAddress.state',
                      id: 'mailingAddress.state',
                      'aria-labelledby': 'mailingAddress.state',
                    },
                  },
                  street1: {
                    autoComplete: 'shipping address-line1',
                    disabled: isLoading,
                    label: (
                      <span className='fs-unmask'>
                        <FormattedMessage
                          defaultMessage='Mailing business address'
                          id='sputnik.BusinessAddressForm__LRoJuV'
                        />
                      </span>
                    ),
                    name: 'mailingAddress.street',
                  },
                  street2: {
                    autoComplete: 'shipping address-line2',
                    disabled: isLoading,
                    label: (
                      <span className='fs-unmask'>
                        <FormattedMessage
                          defaultMessage='Mailing business address line 2'
                          id='sputnik.BusinessAddressForm__SE6E75'
                        />
                      </span>
                    ),
                    name: 'mailingAddress.streetAdditional',
                  },
                  zipCode: {
                    autoComplete: 'shipping postal-code',
                    disabled: isLoading,
                    label: (
                      <span className='fs-unmask'>
                        <FormattedMessage
                          defaultMessage='ZIP code'
                          id='sputnik.BusinessAddressForm__iZL8VQ'
                        />
                      </span>
                    ),
                    name: 'mailingAddress.postalCode',
                  },
                }}
                googleMapsApiKey={window.__DivvyEnvironment.GOOGLE_PLACES_API_KEY}
              />
            )}
          </CreditApplicationPageLayout>
        </div>
      </FormProvider>
    </div>
  )
}

export default BusinessAddressForm
